'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { usePortfolioArrival } from './portfolio-arrival-context';
interface SignUpStartFields {
  emailAddress: string;
  firstName: string;
  lastName: string;
  password: string;
}
interface PortfolioArrivalFields {
  isPortfolioArrival: boolean;
  /** The claim address, locked: adoption matches the new account to the portfolio on this email. */
  lockedEmail: string | null;
  /** True once the portfolio's own name has been written into the form, so the fields can go. */
  namesKnown: boolean;
}
export const PORTFOLIO_ARRIVAL_SUBMIT_LABEL = 'Create account and see my matches';
const INERT: PortfolioArrivalFields = {
  isPortfolioArrival: false,
  lockedEmail: null,
  namesKnown: false
};

/**
 * What both sign-up forms (control and the Q1 experiment) need to know about a portfolio arrival,
 * in one place so the two cannot drift.
 *
 * The values are written with `setValue` rather than seeded through `defaultValues` because the name
 * arrives after first paint. The schema is untouched: a hidden field still has to hold a valid value
 * for the form to submit, which is why the name is only ever hidden once it is one.
 */
export function usePortfolioArrivalFields(setValue, getValues) {
  const $ = _c(14);
  const context = usePortfolioArrival();
  const [namesApplied, setNamesApplied] = useState(false);
  const email = context?.arrival.email ?? "";
  const firstName = context?.names?.firstName ?? "";
  const lastName = context?.names?.lastName ?? "";
  let t0;
  let t1;
  if ($[0] !== email || $[1] !== setValue) {
    t0 = () => {
      if (email) {
        setValue("emailAddress", email, {
          shouldValidate: true
        });
      }
    };
    t1 = [email, setValue];
    $[0] = email;
    $[1] = setValue;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== firstName || $[5] !== getValues || $[6] !== lastName || $[7] !== namesApplied || $[8] !== setValue) {
    t2 = () => {
      if (!(firstName && lastName) || namesApplied) {
        return;
      }
      const {
        firstName: typedFirst,
        lastName: typedLast
      } = getValues();
      if (typedFirst.trim() || typedLast.trim()) {
        return;
      }
      setValue("firstName", firstName, {
        shouldValidate: true
      });
      setValue("lastName", lastName, {
        shouldValidate: true
      });
      setNamesApplied(true);
    };
    t3 = [firstName, getValues, lastName, namesApplied, setValue];
    $[4] = firstName;
    $[5] = getValues;
    $[6] = lastName;
    $[7] = namesApplied;
    $[8] = setValue;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  if (!context) {
    return INERT;
  }
  const t4 = email || null;
  let t5;
  if ($[11] !== namesApplied || $[12] !== t4) {
    t5 = {
      isPortfolioArrival: true,
      lockedEmail: t4,
      namesKnown: namesApplied
    };
    $[11] = namesApplied;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}